import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {
  CampaignActionAlerts,
  CampaignAddHealth,
  CampaignHealthCode,
  HomeStat,
  ListData,
  ListResponse,
} from 'utils/types';
import {addResultCases} from 'utils/helpers';
import ApiService from 'redux/services/api-service';
import {digitalShelfEndpoints} from '../digital-shelf/slice';

// type HomeCard = {title: string; subTitle: string; value: number; color?: string; key: keyof HomeStat};

const initialListData = {count: 0, loading: false, results: []};

export interface HomeState {
  campaignData: ListResponse<CampaignAddHealth>;
  allActionAlerts: ListData<CampaignActionAlerts>;
  cards: Record<string, any>;
  cardsLoading: boolean;
}

export const basicChecklist = {
  rfp_submitted: 'RFP Submitted',
  communicated_with_customer: 'RFP Evaluated',
  initial_sales_uploaded: 'Initial Sales Uploaded',
  confirmed_campaign_details: 'Confirm Campaign Details',
  item_groups_selected: 'Item Groups Selected',
  test_control_selected: 'Test Control Selected',
  weekly_sales_uploaded: 'Weekly Sales Uploaded',
  sales_lift_analysis_run: 'Sales Lift Analysis ran',
  campaign_region_consistent: 'Campaign Region Consistent',
} as const;

// this can be removed if the backend API takes all these factors into account
function transformHealthChecklist(campaignHealth: CampaignAddHealth) {
  const cloneCheckList = {...basicChecklist} as {
    [K in CampaignHealthCode]: CampaignAddHealth['health_checklist_items'][number] | string;
  };
  campaignHealth.health_checklist_items.forEach(item => {
    cloneCheckList[item.health_checklist_item.code] = item;
  });
  campaignHealth.health_checklist_items = Object.entries(cloneCheckList).map(([code, item], position) =>
    typeof item === 'string' ? {stage: 'gray', health_checklist_item: {name: item, position, code}} : item,
  ) as any;
}

export const homeEndpoints = {
  getCards: createAsyncThunk('home/getCards', async (params: any) => {
    const response = await ApiService.get<HomeStat>(`/home/cards/?${new URLSearchParams(params)}`);
    return response.data;
  }),
  getCampaigns: createAsyncThunk('home/getCampaigns', async (params: any) => {
    const response = await ApiService.get<ListResponse<CampaignAddHealth>>(
      `/home/campaigns/?${new URLSearchParams(params)}`,
    );
    response.data.results.forEach(transformHealthChecklist);
    return response.data;
  }),
  exportCampaigns: createAsyncThunk('home/exportCampaigns', async (params: any) => {
    const response = await ApiService.get<Blob>(`/home/campaigns_export/?${new URLSearchParams(params)}`, {
      responseType: 'blob',
      timeout: 600_000,
    });
    return response.data;
  }),
  getActionAlerts: createAsyncThunk('home/getActionAlerts', async (params: any) => {
    if (!params.campaign_id_in) delete params.campaign_id_in;
    const response = await ApiService.get<ListData<CampaignActionAlerts>>(
      `/health_alerts/?${new URLSearchParams(params)}`,
    );
    return response.data;
  }),
};
export const homeSlice = createSlice({
  name: 'home',
  initialState: {
    campaignData: {as: 'idle', count: 0, results: []},
    cards: {},
    cardsLoading: false,
    allActionAlerts: {...initialListData},
  } as HomeState,
  extraReducers: builder => {
    addResultCases(builder, homeEndpoints.getCampaigns, 'campaignData');
    builder
      .addCase(homeEndpoints.getCards.pending, state => {
        state.cardsLoading = true;
      })
      .addCase(homeEndpoints.getCards.fulfilled, (state, {payload}) => {
        state.cards = payload;
        state.cardsLoading = false;
      })
      .addCase(digitalShelfEndpoints.getCampaignItemList.rejected, state => {
        state.cardsLoading = false;
        state.cards = [];
      })
      .addCase(homeEndpoints.getActionAlerts.pending, state => {
        state.allActionAlerts.loading = true;
      })
      .addCase(homeEndpoints.getActionAlerts.fulfilled, (state, {payload}) => {
        state.allActionAlerts = {...payload, loading: false};
      })
      .addCase(homeEndpoints.getActionAlerts.rejected, state => {
        state.allActionAlerts.loading = false;

        // state.cards = [];
      });
  },
  reducers: {},
});

export const homeActions = homeSlice.actions;

export type HomeSlice = {
  [homeSlice.name]: ReturnType<(typeof homeSlice)['reducer']>;
};
